import { Link, NavLink, Navigate, Outlet, useParams } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useCalendars } from '@/hooks/useCalendars';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { Button } from '@/components/ui/Button';
import { AssistantWidget } from '@/components/AssistantWidget';
import { EldersDropdown } from '@/components/EldersDropdown';
import { NoDeviceBanner } from '@/components/NoDeviceBanner';
import { Spinner } from '@/components/ui/Spinner';
import { logOut } from '@/lib/auth';
import { cn } from '@/lib/cn';

export function AppShell() {
  const auth = useAuth();
  const params = useParams<{ aid?: string }>();
  const aid = params.aid ?? null;
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships, loading: membershipsLoading } = useMyMemberships(uid);

  if (auth.state !== 'signedIn') return null;

  // Membership guard. Block descendant pages — and the useCalendars
  // call below — from firing snapshot listeners against an `aid` the
  // current user doesn't belong to. Without this, navigating to a
  // stale URL (eg. /accounts/{otherAid}/calendars/.../events, or a
  // forwarded `next` after signup) would mount the page and fire
  // queries that the server eventually rejects — flashing a
  // half-rendered page in the meantime. (The client cache is in-memory
  // only, so there's no cross-session data leak; this guard is about
  // not mounting pages for an `aid` you don't belong to.)
  if (aid && membershipsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Spinner />
      </div>
    );
  }
  if (aid && !memberships.some((m) => m.id === aid)) {
    return <Navigate to="/" replace />;
  }

  return <AppShellInner aid={aid} />;
}

function AppShellInner({ aid }: { aid: string | null }) {
  const { calendars } = useCalendars(aid);
  const primaryCid = (calendars.find((c) => c.isPrimary) ?? calendars[0])?.id;

  return (
    // flex-col on mobile so MobileTopBar (w-full) stacks above main
    // instead of competing for horizontal space in a row layout —
    // without this the header was eating the whole viewport width
    // and squashing main to 0.
    <div className="flex min-h-screen flex-col bg-paper md:flex-row">
      {/* Mobile top bar — visible only on narrow screens. The desktop
          sidebar below has the same links + EldersDropdown + sign-out;
          they're rendered separately because the layouts diverge enough
          that sharing one element with responsive flex direction got
          messy. */}
      <MobileTopBar aid={aid} primaryCid={primaryCid ?? null} />

      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-paper-border bg-white p-4 md:flex">
        <Link
          to="/"
          className="mb-4 flex items-center gap-2 px-2 text-lg font-semibold text-accent-700"
          aria-label="RemindMeVoice home"
        >
          <img src="/favicon.svg" alt="" width={24} height={24} className="h-6 w-6" />
          RemindMeVoice
        </Link>
        <div className="mb-4">
          <EldersDropdown />
        </div>
        {aid && (
          <nav className="flex flex-col gap-1">
            <SideLink to={`/accounts/${aid}/voice`} label="Voice" />
            {primaryCid ? (
              <SideLink
                to={`/accounts/${aid}/calendars/${primaryCid}/events`}
                label="Calendar"
              />
            ) : (
              <span className="cursor-not-allowed rounded-md px-2 py-1.5 text-sm text-ink-muted">
                Calendar
              </span>
            )}
            <SideLink to={`/accounts/${aid}/activity`} label="Activity" />
            <SideLink to={`/accounts/${aid}/settings`} label="Settings" />
          </nav>
        )}
        <div className="mt-auto space-y-2 border-t border-paper-border pt-3">
          <a
            href="mailto:michael.d.pedersen@googlemail.com?subject=RemindMeVoice%20support"
            className="block rounded-md px-2 py-1.5 text-sm text-ink-soft hover:bg-paper-alt"
          >
            Need help?
          </a>
          <Button variant="ghost" className="w-full justify-start" onClick={() => void logOut()}>
            Sign out
          </Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 md:px-10 md:py-10">
        <NoDeviceBanner aid={aid} />
        <Outlet />
      </main>
      {/* In-app help assistant — only with an account in scope, since its
          tools read that account. */}
      {aid && <AssistantWidget aid={aid} primaryCid={primaryCid ?? null} />}
    </div>
  );
}

function MobileTopBar({
  aid,
  primaryCid,
}: {
  aid: string | null;
  primaryCid: string | null;
}) {
  return (
    <header className="sticky top-0 z-10 flex w-full items-center gap-3 overflow-x-auto border-b border-paper-border bg-white px-3 py-2 md:hidden">
      <Link
        to="/"
        className="flex shrink-0 items-center gap-1.5 text-base font-semibold text-accent-700"
        aria-label="RemindMeVoice home"
      >
        <img src="/favicon.svg" alt="" width={20} height={20} className="h-5 w-5" />
        RMV
      </Link>
      <div className="shrink-0">
        <EldersDropdown />
      </div>
      {aid && (
        <nav className="flex shrink-0 items-center gap-1 text-sm">
          <MobileNavLink to={`/accounts/${aid}/voice`}>Voice</MobileNavLink>
          {primaryCid && (
            <MobileNavLink to={`/accounts/${aid}/calendars/${primaryCid}/events`}>
              Calendar
            </MobileNavLink>
          )}
          <MobileNavLink to={`/accounts/${aid}/activity`}>Activity</MobileNavLink>
          <MobileNavLink to={`/accounts/${aid}/settings`}>Settings</MobileNavLink>
        </nav>
      )}
      <button
        className="ml-auto shrink-0 rounded-md px-2 py-1 text-xs text-ink-soft hover:bg-paper-alt"
        onClick={() => void logOut()}
      >
        Sign out
      </button>
    </header>
  );
}

function MobileNavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-2 py-1 hover:bg-paper-alt',
          isActive ? 'bg-accent-50 text-accent-700' : 'text-ink-soft',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function SideLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-2 py-1.5 text-sm text-ink-soft hover:bg-paper-alt',
          isActive && 'bg-accent-50 text-accent-700',
        )
      }
    >
      {label}
    </NavLink>
  );
}
